import React, { useEffect, useState } from "react";
import { MdArticle, MdBusiness, MdContacts, MdDevices, MdEmail, MdInsights, MdKeyboardArrowDown, MdManageAccounts, MdMonitorHeart, MdOutlineGroups, MdSystemUpdate } from "react-icons/md";
import { CiLogout } from "react-icons/ci";
import AdminUsageSection from "../components/analytics/AdminUsageSection";
import { IoHomeOutline } from "react-icons/io5";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DeviceUpdates from "../components/admin/DeviceUpdates";
import DeviceMonitoring from "../components/admin/DeviceMonitoring";
import { lightOnline } from "../components/admin/lightOrganization";
import DeviceProvisioningModal from "../components/admin/DeviceProvisioningModal";
import OrganizationCreateModal from "../components/admin/OrganizationCreateModal";
import VoiceModelModal from "../components/admin/VoiceModelModal";
import {
  NEWSLETTER_AGE_GROUPS,
  NEWSLETTER_AUDIENCES,
  NEWSLETTER_BENEFITS,
  newsletterOptionLabel,
} from "../config/newsletterSurvey";

const CreatorDashboard = React.lazy(() => import("./creator/CreatorDashboard"));
const BlogEditorPage = React.lazy(() => import("./creator/BlogEditorPage"));
const CrmPage = React.lazy(() => import("../features/crm/CrmPage"));

const API = process.env.REACT_APP_API_URL;

function formatLastInteraction(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString("de-DE");
}

function PatientPresence({ patient, compact = false, hideLastActive = false }) {
  const isActive = Boolean(patient.is_online);
  const isConnected = Boolean(patient.is_device_online);
  const label = isActive ? "Active" : isConnected ? "Online" : "Offline";
  const colors = isActive
    ? "bg-green-100 text-green-700"
    : isConnected
      ? "bg-orange-100 text-orange-800"
      : "bg-gray-100 text-gray-600";

  return (
    <div className="shrink-0 text-right">
      <span className={`inline-block rounded-full px-3 py-1 font-medium ${compact ? "text-xs" : "text-sm"} ${colors}`}>
        {label}
      </span>
      {isConnected && !isActive && !hideLastActive && (
        <div className="mt-1 text-xs text-gray-500">
          Last active: {formatLastInteraction(patient.last_seen_at)}
        </div>
      )}
    </div>
  );
}

const VIEW_LABELS = {
  patients: ["All Patients", "Patients"],
  organizations: ["All Organizations", "Organizations"],
  users: ["All Users", "Users"],
  devices: ["All Devices", "Devices"],
  "newsletter-subscribers": ["Newsletter Subscribers", "Subscribers"],
  "internal-admins": ["Internal Admins", "Internal Admins"],
  usage: ["Device Usage", "Residents"],
};

function formatAdminDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("de-DE");
}

function NewsletterSurveySummary({ subscribers }) {
  const [expanded, setExpanded] = useState(false);
  const [audience, setAudience] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const responses = subscribers.filter((item) => item.survey_completed_at);
  const filtered = responses.filter((item) =>
    (!audience || item.survey_answers?.audience === audience)
    && (!ageGroup || item.survey_answers?.age_group === ageGroup));

  function breakdown(title, options, values, ranked = false) {
    const rows = options.map((option) => {
      const matches = values.filter((value) => ranked ? value.includes(option.value) : value === option.value);
      const averageRank = ranked && matches.length
        ? values.reduce((sum, value) => {
            const index = value.indexOf(option.value);
            return sum + (index >= 0 ? index + 1 : 0);
          }, 0) / matches.length
        : null;
      return { ...option, count: matches.length, averageRank };
    });
    const maximum = Math.max(1, ...rows.map((row) => row.count));

    return (
      <section className="rounded-xl border border-black/10 bg-white/80 p-4">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <div className="mt-4 space-y-3">
          {rows.map((row) => (
            <div key={row.value}>
              <div className="flex justify-between gap-3 text-xs text-gray-700">
                <span>{row.de}</span>
                <span className="shrink-0">{row.count} · {filtered.length ? Math.round((row.count / filtered.length) * 100) : 0}%{row.averageRank ? ` · Ø Rang ${row.averageRank.toFixed(1)}` : ""}</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-[#76B3FF]" style={{ width: `${(row.count / maximum) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="mt-6 rounded-xl border border-black/10 bg-white/60 p-4">
      <button type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} className="flex w-full items-center justify-between gap-4 text-left">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Umfrageergebnisse</h2>
          <p className="text-sm text-gray-600">{responses.length} von {subscribers.length} Abonnenten haben die Fragen beantwortet.</p>
        </div>
        <MdKeyboardArrowDown size={26} className={`shrink-0 text-gray-600 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && <>
      <div className="mt-4 flex flex-wrap items-end justify-end gap-4">
        <div className="flex flex-wrap gap-3">
          <select value={audience} onChange={(event) => setAudience(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
            <option value="">Alle Zielgruppen</option>
            {NEWSLETTER_AUDIENCES.map((option) => <option key={option.value} value={option.value}>{option.de}</option>)}
          </select>
          <select value={ageGroup} onChange={(event) => setAgeGroup(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
            <option value="">Alle Altersgruppen</option>
            {NEWSLETTER_AGE_GROUPS.map((option) => <option key={option.value} value={option.value}>{option.de}</option>)}
          </select>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium text-gray-700">{filtered.length} Antworten in der aktuellen Auswahl</p>
      <div className="mt-4 grid gap-4 xl:grid-cols-3">
        {breakdown("Für wen?", NEWSLETTER_AUDIENCES, filtered.map((item) => item.survey_answers?.audience))}
        {breakdown("Altersgruppe", NEWSLETTER_AGE_GROUPS, filtered.map((item) => item.survey_answers?.age_group))}
        {breakdown("Größter Mehrwert", NEWSLETTER_BENEFITS, filtered.map((item) => item.survey_answers?.priorities || []), true)}
      </div>
      </>}
    </div>
  );
}

function PatientDetail({ patient, onBack, onProvisionDevice, provisioningDevice }) {
  const facts = [
    ["Patient ID", patient.id],
    ["Device ID", patient.device?.public_id || "—"],
    ["Device status", patient.device?.status || "No device"],
    ["Age", patient.age],
    ["Organization", patient.organization_name || "No organization"],
    ["Organization ID", patient.organization_id || "—"],
    ["Residency city", patient.residency_city || "—"],
    ["Caregiver", patient.betreuer || "—"],
    ["Language", patient.language || "—"],
    ["Voice", patient.voice || "—"],
    ["Device", patient.is_device_online ? "Online" : "Offline"],
    ["Last seen", patient.last_seen_at ? new Date(patient.last_seen_at).toLocaleString("de-DE") : "Never"],
  ];
  const lists = [
    ["Interests", patient.interests],
    ["Biography facts", patient.bio_facts],
    ["Radio stations", patient.radio_stations?.map((station) => station.name)],
  ];

  return (
    <>
      <button onClick={onBack} className="mb-5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">← All Patients</button>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-500">Patient overview</p>
          <h1 className="text-3xl font-semibold text-gray-900">{patient.forename} {patient.surname}</h1>
        </div>
        <div className="flex items-center gap-3">
          {!patient.device && <button type="button" disabled={provisioningDevice} onClick={onProvisionDevice} className="rounded-lg bg-[#76B3FF] px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-60">{provisioningDevice ? "Provisioning…" : "Assign enrolled device"}</button>}
          <PatientPresence patient={patient} />
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {facts.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-black/10 bg-white/80 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
            <div className="mt-1 text-gray-900">{value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="rounded-xl border border-black/10 bg-white/80 p-5">
          <h2 className="font-semibold text-gray-900">Biography</h2>
          <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{patient.biography || "No biography stored."}</p>
          {patient.bio_path && <div className="mt-3 text-xs text-gray-500">File: {patient.bio_path}</div>}
        </section>
        {lists.map(([label, values]) => (
          <section key={label} className="rounded-xl border border-black/10 bg-white/80 p-5">
            <h2 className="font-semibold text-gray-900">{label}</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {values?.length ? values.map((value, index) => <span key={index} className="rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800">{typeof value === "string" ? value : JSON.stringify(value)}</span>) : <span className="text-sm text-gray-600">No data stored.</span>}
            </div>
          </section>
        ))}
        <section className="rounded-xl border border-black/10 bg-white/80 p-5">
          <h2 className="font-semibold text-gray-900">Meal preferences</h2>
          <pre className="mt-3 overflow-auto whitespace-pre-wrap text-xs text-gray-700">{JSON.stringify(patient.meal_preferences || {}, null, 2)}</pre>
        </section>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  const isBlog = location.pathname.startsWith("/admin/blog");
  const isCrm = location.pathname.startsWith("/admin/crm");
  const isBlogEditor = location.pathname.endsWith("/edit") || location.pathname.endsWith("/new");
  const [listView, setView] = useState("patients");
  const view = location.pathname.startsWith("/admin/updates") ? "updates" : location.pathname.startsWith("/admin/monitoring") ? "monitoring" : listView;
  const [items, setItems] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [membership, setMembership] = useState("all");
  const [organizationId, setOrganizationId] = useState("");
  // Light organization: a frontend-only demo mode (no Device Usage, half of patients and devices shown online).
  const light = membership === "light";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [patient, setPatient] = useState(null);
  const [showProvisioning, setShowProvisioning] = useState(false);
  const [showOrganizationCreation, setShowOrganizationCreation] = useState(false);
  const [modelDevice, setModelDevice] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const provisioningDevice = false;
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [internalAdminEmail, setInternalAdminEmail] = useState("");
  const [savingInternalAdmin, setSavingInternalAdmin] = useState(false);
  const [newsletterType, setNewsletterType] = useState("all");
  const [newsletterCity, setNewsletterCity] = useState("");

  useEffect(() => {
    // Usage, updates and monitoring load their own data.
    if (patientId || isBlog || isCrm || view === "usage" || view === "updates" || view === "monitoring") return;
    let cancelled = false;
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (view === "patients") {
      params.set("membership", membership === "light" ? "all" : membership);
      if (membership === "assigned" && organizationId) params.set("organization_id", organizationId);
    }
    const loadItems = () => {
      fetch(`${API}/admin/${view}${params.size ? `?${params}` : ""}`, { credentials: "include" })
        .then((response) => {
          if (!response.ok) throw new Error("Data could not be loaded.");
          return response.json();
        })
        .then((data) => {
          if (!cancelled) {
            setItems(Array.isArray(data) ? data : []);
            setError("");
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err.message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    loadItems();
    const interval = view === "patients" ? window.setInterval(loadItems, 15000) : null;
    return () => {
      cancelled = true;
      if (interval) window.clearInterval(interval);
    };
  }, [view, membership, organizationId, patientId, isBlog, isCrm, reloadKey]);

  useEffect(() => {
    if (!patientId) {
      setPatient(null);
      return;
    }
    setLoading(true);
    setError("");
    fetch(`${API}/admin/patients/${patientId}`, { credentials: "include" })
      .then((response) => {
        if (!response.ok) throw new Error("Patient data could not be loaded.");
        return response.json();
      })
      .then(setPatient)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => {
    fetch(`${API}/me`, { credentials: "include" })
      .then((response) => response.ok ? response.json() : {})
      .then((data) => {
        setIsSuperuser(data.superuser === true);
        setCurrentUserId(data.id ?? null);
      });
    fetch(`${API}/admin/organizations`, { credentials: "include" })
      .then((response) => response.ok ? response.json() : [])
      .then((data) => setOrganizations(Array.isArray(data) ? data : []));
  }, []);

  async function logout() {
    await fetch(`${API}/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    navigate("/login", { replace: true });
  }

  async function addInternalAdmin(event) {
    event.preventDefault();
    if (!internalAdminEmail.trim() || savingInternalAdmin) return;
    setSavingInternalAdmin(true);
    setError("");
    try {
      const response = await fetch(`${API}/admin/internal-admins`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: internalAdminEmail }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Internal admin could not be added.");
      setInternalAdminEmail("");
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingInternalAdmin(false);
    }
  }

  async function removeInternalAdmin(item) {
    if (!window.confirm(`Remove internal admin access for ${item.email}?`)) return;
    setError("");
    try {
      const response = await fetch(`${API}/admin/internal-admins/${item.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Internal admin could not be removed.");
      setItems((current) => current.filter((entry) => entry.id !== item.id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteAdminItem(type, item) {
    const labels = {
      patients: `${item.forename || ""} ${item.surname || ""}`.trim() || `patient ${item.id}`,
      users: item.email,
      devices: item.public_id,
    };
    if (!window.confirm(`Permanently delete ${labels[type]}?`)) return;
    setError("");
    try {
      const response = await fetch(`${API}/admin/${type}/${item.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "The item could not be deleted.");
      setItems((current) => current.filter((entry) => entry.id !== item.id));
    } catch (err) {
      setError(err.message);
    }
  }

  function exportNewsletterSubscribers() {
    const emails = items
      .filter((subscriber) => subscriber.confirmed_at)
      .map((subscriber) => subscriber.email)
      .filter(Boolean);
    if (!emails.length) return;
    const blob = new Blob([`${emails.join("\n")}\n`], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `newsletter-subscribers-${new Date().toISOString().slice(0, 10)}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  function provisionExistingDevice() {
    setShowProvisioning(true);
  }

  function organizationCreated(organization) {
    const addAndSort = (current) => [...current, organization]
      .sort((left, right) => left.name.localeCompare(right.name));
    setOrganizations(addAndSort);
    setItems(addAndSort);
  }

  const newsletterCities = [...new Set(items.map((item) => item.city).filter(Boolean))].sort();
  const visibleItems = view === "newsletter-subscribers"
    ? items.filter((item) =>
        (!newsletterCity || item.city === newsletterCity)
        && (newsletterType === "all"
          || (newsletterType === "waitlist" && item.waitlist_joined_at)
          || (newsletterType === "newsletter" && (item.confirmation_sent_at || item.confirmed_at))))
    : view === "patients" && light
      ? lightOnline(items, (item) => Boolean(item.is_online || item.is_device_online), (item, online) => ({ ...item, is_online: false, is_device_online: online }))
      : items;

  return (
    <div className="flex min-h-screen bg-[#cdeaff]">
      <aside className="sticky top-0 flex h-screen w-[260px] shrink-0 flex-col bg-[#cdeaff]">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl ring-1 ring-indigo-200">
            <img src="/steffi/logo.png" alt="SteffiCare" className="h-full w-full object-cover" />
          </div>
          <span className="text-sm font-semibold text-gray-800">SteffiCare Admin</span>
        </div>
        <div className="mx-4 mb-4 h-px bg-black/10" />
        <nav className="space-y-1 px-4">
          <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Admin</h3>
          <button onClick={() => { setView("patients"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "patients" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdOutlineGroups />
            <span>All Patients</span>
          </button>
          <button onClick={() => { setView("organizations"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "organizations" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdBusiness />
            <span>Organizations</span>
          </button>
          <button onClick={() => { setView("users"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "users" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdManageAccounts />
            <span>All Users</span>
          </button>
          <button onClick={() => { setView("devices"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "devices" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdDevices />
            <span>All Devices</span>
          </button>
          <button onClick={() => navigate("/admin/updates")} aria-current={view === "updates" ? "page" : undefined} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${view === "updates" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdSystemUpdate />
            <span>Updates</span>
          </button>
          <button onClick={() => navigate("/admin/monitoring")} aria-current={view === "monitoring" ? "page" : undefined} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${view === "monitoring" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdMonitorHeart />
            <span>Monitoring</span>
          </button>
          {!light && (
            <button onClick={() => { setView("usage"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "usage" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
              <MdInsights />
              <span>Device Usage</span>
            </button>
          )}
          <button onClick={() => { setView("newsletter-subscribers"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "newsletter-subscribers" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdEmail />
            <span>Newsletter</span>
          </button>
          {isSuperuser && (
            <button onClick={() => { setView("internal-admins"); navigate("/admin"); }} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${!isBlog && !isCrm && view === "internal-admins" ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
              <MdManageAccounts />
              <span>Internal Admins</span>
            </button>
          )}
          <button onClick={() => navigate("/admin/blog")} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${isBlog ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdArticle />
            <span>Blog</span>
          </button>
          <button onClick={() => navigate("/admin/crm")} className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm ${isCrm ? "bg-[#76B3FF] text-white shadow" : "text-gray-700 hover:bg-white/70"}`}>
            <MdContacts />
            <span>CRM</span>
          </button>
        </nav>
        <div className="flex-1" />
        <div className="px-4 pb-4">
          <button onClick={logout} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-white/70">
            <CiLogout />
            <span>Log out</span>
          </button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 pt-6">
        <div className="h-full min-w-0 min-h-[calc(100vh-1.5rem)] overflow-clip rounded-tl-xl border border-black/10 bg-white/60 p-6 backdrop-blur">
          {isCrm ? (
            <CrmPage />
          ) : isBlogEditor ? (
            <div className="creator-app -m-6"><BlogEditorPage basePath="/admin" /></div>
          ) : isBlog ? (
            <div className="creator-app -m-6"><CreatorDashboard basePath="/admin" /></div>
          ) : patientId ? (
            <>
              {loading && <div className="text-sm text-gray-700">Loading patient…</div>}
              {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
              {!loading && patient && <PatientDetail patient={patient} onBack={() => navigate("/admin")} onProvisionDevice={provisionExistingDevice} provisioningDevice={provisioningDevice} />}
            </>
          ) : view === "usage" ? (
            <AdminUsageSection />
          ) : view === "updates" ? (
            <DeviceUpdates light={light} />
          ) : view === "monitoring" ? (
            <DeviceMonitoring light={light} />
          ) : (
          <>
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-3xl font-semibold text-gray-900">{VIEW_LABELS[view][0]}</h1>
            <div className="flex items-center gap-4">
              {view === "patients" && <button type="button" onClick={() => setShowProvisioning(true)} className="rounded-lg bg-[#76B3FF] px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-500">Assign device</button>}
              {view === "organizations" && <button type="button" onClick={() => setShowOrganizationCreation(true)} className="rounded-lg bg-[#76B3FF] px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-500">Create organization</button>}
              {view === "newsletter-subscribers" && (
                <button
                  type="button"
                  onClick={exportNewsletterSubscribers}
                  disabled={!items.some((subscriber) => subscriber.confirmed_at)}
                  className="rounded-lg bg-[#76B3FF] px-4 py-2 text-sm font-medium text-white shadow hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Export confirmed (.txt)
                </button>
              )}
              <div className="flex items-center gap-2 text-gray-800">
                <IoHomeOutline />
                <span className="text-lg">{visibleItems.length} {VIEW_LABELS[view][1]}</span>
              </div>
            </div>
          </div>

          {view === "newsletter-subscribers" && !loading && !error && (
            <>
              <div className="mt-3 text-sm text-gray-600">
                {items.filter((subscriber) => subscriber.confirmed_at).length} confirmed · {items.filter((subscriber) => subscriber.waitlist_joined_at).length} waitlist
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <select value={newsletterType} onChange={(event) => setNewsletterType(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
                  <option value="all">All signups</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="waitlist">Waitlist</option>
                </select>
                <select value={newsletterCity} onChange={(event) => setNewsletterCity(event.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
                  <option value="">All cities</option>
                  {newsletterCities.map((city) => <option key={city} value={city}>{city}</option>)}
                </select>
              </div>
              <NewsletterSurveySummary subscribers={visibleItems} />
            </>
          )}

          {view === "internal-admins" && (
            <form onSubmit={addInternalAdmin} className="mt-6 flex flex-wrap gap-3 rounded-xl border border-black/10 bg-white/70 p-4">
              <input
                type="email"
                required
                value={internalAdminEmail}
                onChange={(event) => setInternalAdminEmail(event.target.value)}
                placeholder="Email address"
                className="min-w-72 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:border-[#76B3FF]"
              />
              <button type="submit" disabled={savingInternalAdmin} className="rounded-lg bg-[#76B3FF] px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50">
                {savingInternalAdmin ? "Adding…" : "Add internal admin"}
              </button>
            </form>
          )}

          {view === "patients" && (
            <div className="mt-6 flex flex-wrap items-end gap-4 rounded-xl border border-black/10 bg-white/70 p-4">
              <label className="flex min-w-56 flex-col gap-1 text-sm font-medium text-gray-700">
                Organization membership
                <select
                  value={membership}
                  onChange={(event) => {
                    setMembership(event.target.value);
                    setOrganizationId("");
                  }}
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:border-[#76B3FF]"
                >
                  <option value="all">All patients</option>
                  <option value="assigned">With organization</option>
                  <option value="unassigned">Without organization</option>
                  <option value="light">Light organization</option>
                </select>
              </label>

              {membership === "assigned" && (
                <label className="flex min-w-64 flex-col gap-1 text-sm font-medium text-gray-700">
                  Organization
                  <select
                    value={organizationId}
                    onChange={(event) => setOrganizationId(event.target.value)}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 outline-none focus:border-[#76B3FF]"
                  >
                    <option value="">All organizations</option>
                    {organizations.map((organization) => (
                      <option key={organization.id} value={organization.id}>{organization.name}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {loading && <div className="mt-6 text-sm text-gray-700">Loading accounts…</div>}
          {error && <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
          {!loading && !error && visibleItems.length === 0 && <div className="mt-6 text-sm text-gray-700">No {view} registered.</div>}

          <div className="mt-6 overflow-hidden rounded-xl border border-black/10 bg-white/80 shadow-sm">
            {visibleItems.map((item) => view === "organizations" ? (
                <a key={item.id} href={`/übersicht?admin_organization_id=${item.id}${light ? "&light=1" : ""}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 border-b border-black/10 p-4 transition hover:bg-blue-50/70 last:border-b-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-800"><MdBusiness size={24} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-gray-900">{item.name}</div>
                    {item.residency_city && <div className="truncate text-sm text-gray-600">{item.residency_city}</div>}
                  </div>
                  <span className="rounded-lg bg-[#76B3FF] px-3 py-2 text-sm font-medium text-white">Open dashboard</span>
                </a>
              ) : view === "patients" ? (
                <div key={item.id} className="flex items-center border-b border-black/10 last:border-b-0">
                  <button type="button" onClick={() => navigate(`/admin/patients/${item.id}`)} className="flex min-w-0 flex-1 items-center gap-4 p-4 text-left transition hover:bg-blue-50/70">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-black/80 text-lg font-semibold">{item.forename?.[0]}{item.surname?.[0]}</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-900">{item.forename} {item.surname}</div>
                      <div className="text-sm text-gray-600">{item.organization_name || "No organization"} · Age {item.age}</div>
                    </div>
                    <PatientPresence patient={item} compact hideLastActive={light} />
                  </button>
                  <button type="button" onClick={() => deleteAdminItem("patients", item)} className="mr-4 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50">Remove</button>
                </div>
              ) : view === "users" ? (
                <div key={item.id} className="flex items-center gap-4 border-b border-black/10 p-4 last:border-b-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 font-semibold text-indigo-800">{item.email?.[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-gray-900">{item.email}</div>
                    <div className="text-sm text-gray-600">{item.organization_name || "No organization"}</div>
                  </div>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">{item.superuser ? "Super Admin" : (item.role || "No role")}</span>
                  {!item.superuser && item.id !== currentUserId && <button type="button" onClick={() => deleteAdminItem("users", item)} className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50">Remove</button>}
                </div>
              ) : view === "devices" ? (
                <div key={item.id} className="flex items-center gap-4 border-b border-black/10 p-4 last:border-b-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-800"><MdDevices size={24} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900">{item.public_id}</div>
                    <div className="text-sm text-gray-600">{item.patient_name || "Awaiting private pairing"} · {item.organization_name || "Private"}</div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${item.status === "active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>{item.status}</span>
                  <button type="button" disabled={!item.patient_id} title={item.patient_id ? undefined : "No resident assigned"} onClick={() => setModelDevice(item)} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Voice</button>
                  <button type="button" onClick={() => deleteAdminItem("devices", item)} className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50">Remove</button>
                </div>
              ) : view === "newsletter-subscribers" ? (
                <div key={item.id} className="flex items-center gap-4 border-b border-black/10 p-4 last:border-b-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 font-semibold text-blue-800">{item.email?.[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-gray-900">{item.email}</div>
                    <div className="text-sm text-gray-600">
                      Subscribed {formatAdminDate(item.created_at)} · {item.language?.toUpperCase() || "No language"}
                    </div>
                    {(item.city || item.phone) && <div className="text-sm text-gray-600">{[item.city, item.phone].filter(Boolean).join(" · ")}</div>}
                    {item.survey_completed_at && (
                      <div className="mt-1 text-xs text-gray-500">
                        {newsletterOptionLabel(NEWSLETTER_AUDIENCES, item.survey_answers?.audience)} · {newsletterOptionLabel(NEWSLETTER_AGE_GROUPS, item.survey_answers?.age_group)} · {(item.survey_answers?.priorities || []).map((value) => newsletterOptionLabel(NEWSLETTER_BENEFITS, value)).join(" → ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 text-right">
                    {item.waitlist_joined_at && <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">Waitlist</span>}
                    {(item.confirmation_sent_at || item.confirmed_at) && <span className={`rounded-full px-3 py-1 text-xs font-medium ${item.confirmed_at ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>
                      {item.confirmed_at ? "Confirmed" : "Pending"}
                    </span>}
                    {item.confirmed_at && <div className="mt-1 text-xs text-gray-500">{formatAdminDate(item.confirmed_at)}</div>}
                  </div>
                </div>
              ) : (
                <div key={item.id} className="flex items-center gap-4 border-b border-black/10 p-4 last:border-b-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 font-semibold text-indigo-800">{item.email?.[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-gray-900">{item.email}</div>
                    <div className="text-sm text-gray-600">Added {formatAdminDate(item.created_at)}</div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${item.active ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-800"}`}>
                    {item.active ? "Active" : "Pending registration"}
                  </span>
                  <button type="button" onClick={() => removeInternalAdmin(item)} className="rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50">
                    Remove
                  </button>
                </div>
              ))}
          </div>
          </>
          )}
        </div>
      </main>
      <DeviceProvisioningModal
        open={showProvisioning}
        organizations={organizations}
        patientId={patientId}
        onClose={() => setShowProvisioning(false)}
        onProvisioned={(data) => {
          if (patientId) setPatient((current) => ({ ...current, device: data.device }));
          setReloadKey((value) => value + 1);
        }}
      />
      <VoiceModelModal device={modelDevice} onClose={() => setModelDevice(null)} />
      <OrganizationCreateModal
        open={showOrganizationCreation}
        onClose={() => setShowOrganizationCreation(false)}
        onCreated={organizationCreated}
      />
    </div>
  );
}
